import { EditOutlined } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  Tooltip,
  Grid,
  Typography,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Paper,
  Container,
  Fade
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ADMIN_ROLES } from "../../utils/constants";
import DataTable from "react-data-table-component";
import { useNavigate, useSearchParams } from "react-router-dom";
import errorHandler from "../../utils/errorHandler";
import { capitalize, TrimText } from "../../utils/helpers";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import { gamePointManagementApi } from "../../services/AllServices";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";
import Iconify from "../../components/iconify";

export default function GamePointList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState();
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalGamePoints, setTotalGamePoints] = useState(0);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL parameter to maintain page state
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page);
    setSearchParams(newSearchParams);
  };


  const columns = [
    {
      name: "S.No.",
      sortable: false,
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: '100px'
    },
    {
      name: "Game Type",
      sortable: true,
      selector: (row) => capitalize(TrimText(row.contestType, 25)),
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(fDate(row.createdAt)),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/game-point-management/view/${row?._id}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {user?.role !== ADMIN_ROLES.JUNIOR && (
            <Tooltip title="Edit" placement="right">
              <IconButton
                color="primary"
                onClick={() => {
                  navigate(`/game-point-management/edit/${row?._id}?page=${currentPage}&view=${viewMode}`);
                }}
              >
                <EditOutlined />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];


  // Initialize currentPage and viewMode from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const viewModeFromUrl = searchParams.get('view');
    
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }
    
    if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
      setViewMode(viewModeFromUrl);
    }
  }, [searchParams]);


  useEffect(() => {
    const body = {
      searchItem: "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: [],
    };
    setIsLoading(true)
    setLoaderDelay(false);
    gamePointManagementApi.getAll(body)
      .then((res) => {
        if (res.status === 200) {
          setTimeout(() => {
            setData(res?.data?.data?.result);
            setTotalPages(res?.data?.data?.totalRecords);
            
            // Calculate stats
            if (res?.data?.data?.result) {
              const gamePoints = res.data.data.result;
              setTotalGamePoints(gamePoints.length);
            }
            
            setLoaderDelay(true);
          }, 500);
        }
      })
      .catch((err) => {
        setIsLoading(false)
        errorHandler(err);
        setLoaderDelay(true);
      })
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false);
      });
  }, [currentPage, pageSize, refresh]);

  const renderGamePointCard = (gamePoint, index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={gamePoint._id}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
          }
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
              boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
            }}
          >
            <SportsEsportsIcon sx={{ fontSize: 24, color: 'white' }} />
          </Avatar>
          <Chip
            label="Game Point"
            size="small"
            sx={{
              backgroundColor: 'rgba(0, 161, 221, 0.2)',
              color: '#00a1dd',
              fontWeight: 600,
              border: '1px solid',
              borderColor: 'rgba(0, 161, 221, 0.3)'
            }}
          />
        </Stack>

        {/* Content */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
            {capitalize(TrimText(gamePoint.contestType, 20))}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Game Point Configuration
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Created: {fDate(gamePoint.createdAt)}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => navigate(`/game-point-management/view/${gamePoint._id}`)}
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
              }
            }}
          >
            View
          </Button>
          {user?.role !== ADMIN_ROLES.JUNIOR && (
            <Button
              size="small"
              variant="contained"
              startIcon={<EditOutlined />}
              onClick={() => navigate(`/game-point-management/edit/${gamePoint._id}?page=${currentPage}&view=${viewMode}`)}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                }
              }}
            >
              Edit
            </Button>
          )}
        </Stack>
      </Paper>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                    boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                  }}
                >
                  <SportsEsportsIcon sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    Game Point Management
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Manage and configure game points for different contest types
                  </Typography>
                </Box>
              </Stack>            
            </Stack>

            {/* Stats Cards - Add your new stats here */}
            <Grid container spacing={3}>
              {/* Add your new stats cards here */}
            </Grid>
          </Paper>

          {/* Controls Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setViewMode(newMode);
                    // Update URL parameter to maintain view mode
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('view', newMode);
                    setSearchParams(newSearchParams);
                  }
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          {/* Content Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box sx={{ p: 3 }}>
                {isLoading || !loaderDelay ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                        boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                      }}
                    >
                      <SportsEsportsIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      No game points found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria or create a new game point configuration
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {data.map((gamePoint, index) => renderGamePointCard(gamePoint, index))}
                    </Grid>
                    
                    {/* Pagination for Grid View */}
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalPages)} of {totalPages} game points
                      </Typography>
                    </Box>
                  </>
                )}
      </Box>
            ) : (
              /* List View */
              <Box>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'black'
              },
            },
            rows: {
              style: {
                backgroundColor: 'transparent',
                color: 'black',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                }
              }
            },
            cells: {
              style: {
                color: 'white',
              }
            }
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
            setPageSize(currentRowsPerPage);
            setCurrentPage(currentPage);
          }}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>
    </Container>
  );
}
